// Cat
// Features:
// - Breathing animation on the cat mesh scale
// - Floating "Zzz" notes above the cat
// - Sound of cat sleeping

import { useRef, useEffect } from 'react'
import { useTexture, Instances, Instance } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import gsap from 'gsap'

import RoomSound from './RoomSound'

import sleepTextureURL from '/textures/sleep.png'

export default function Cat({ nodes, material }) {
    const catRef = useRef()
    const sleepRef = useRef()
    const timelinesRef = useRef([])

    // Textures
    const sleepTexture = useTexture(sleepTextureURL, (texture) => {
        texture.colorSpace = THREE.NoColorSpace
    })

    // Animations: Breathing + Zzz Particles
    useEffect(() => {
        if (!catRef.current || !sleepRef.current) return

        // 1. Breathing Animation
        const base = { x: 1, y: 1, z: 1 } // Assuming base scale is 1

        const breathingTl = gsap.to(catRef.current.scale, {
            x: base.x - 0.03,
            y: base.y + 0.08,
            z: base.z - 0.03,
            duration: 3.2,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
        })

        // 2. Floating Zzz Animation
        sleepRef.current.children.forEach((child, index) => {
            const tl = gsap.timeline({ repeat: -1, delay: index })

            // Float Up
            tl.fromTo(
                child.position,
                { y: 0 },
                { y: 0.8, duration: 4, ease: 'linear' }
            )

            // Pulse Scale
            tl.fromTo(
                child.scale,
                { x: 0, y: 0, z: 0 },
                {
                    x: 1,
                    y: 1,
                    z: 1,
                    duration: 2,
                    ease: 'power2.out',
                    yoyo: true,
                    repeat: 1,
                },
                0
            )

            timelinesRef.current.push(tl)
        })

        return () => {
            breathingTl.kill()
            timelinesRef.current.forEach((tl) => tl && tl.kill())
            timelinesRef.current = []
        }
    }, [])

    // Frame Loop: Billboard particles to camera
    useFrame((state) => {
        if (sleepRef.current) {
            const { clock, camera } = state
            sleepRef.current.children.forEach((child, index) => {
                child.position.x = Math.sin(clock.elapsedTime + index) * 0.04
                child.lookAt(camera.position)
            })
        }
    })

    return (
        <group>
            <mesh
                ref={catRef}
                geometry={nodes.cat.geometry}
                material={material}
                position={[-0.645, 0.088, -3.252]}
            />

            {/* Zzz Particles */}
            <group position={[-0.445, 0.488, -3.252]}>
                <Instances ref={sleepRef} visible>
                    <planeGeometry args={[0.2, 0.2]} />
                    <meshBasicMaterial
                        alphaMap={sleepTexture}
                        transparent={true}
                        opacity={0.5}
                        depthWrite={false}
                    />
                    {Array(3)
                        .fill(null)
                        .map((_, i) => (
                            <Instance key={i} position={[0, 0, 0]} />
                        ))}
                </Instances>
            </group>

            <RoomSound
                url="/sounds/cat.mp3"
                volume={0.2}
                position={[-0.445, 0.488, -3.252]}
            />
        </group>
    )
}
